import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CrudManager from "../../../components/common/CrudManager";
import PageHeader from "../../../components/common/PageHeader";
import { faFileInvoice } from "@fortawesome/free-solid-svg-icons";

import commissionSalesGroupsService from "../../../services/legacy/commissionSalesGroupsService";
import contactsService from "../../../services/legacy/contactsService";
import countiesService from "../../../services/legacy/countiesService";
import countriesService from "../../../services/legacy/countriesService";
import customerPaymentModesService from "../../../services/legacy/customerPaymentModesService";
import customersService from "../../../services/legacy/customersService";
import deliveryModesService from "../../../services/legacy/deliveryModesService";
import itemsService from "../../../services/legacy/itemsService";
import lineofbusinessService from "../../../services/legacy/lineofbusinessService";
import paymentSchedulesService from "../../../services/legacy/paymentSchedulesService";
import priceDiscGroupsService from "../../../services/legacy/priceDiscGroupsService";
import salesOrderLinesService from "../../../services/legacy/salesOrderLinesService";
import salesOrdersService from "../../../services/legacy/salesOrdersService";
import statesService from "../../../services/legacy/statesService";
import taxGroupHeadingService from "../../../services/legacy/taxGroupHeadingService";
import taxItemGroupHeadingService from "../../../services/legacy/taxItemGroupHeadingService";
import unitConvertService from "../../../services/legacy/unitConvertService";

const DOCUMENT_ROUTE_BASE = "/legacy/sales-orders";
const HEADER_ID_FIELD = "iSalesId";
const DATA_AREA_FIELD = "dataAreaId";
const HEADER_SUMMARY_FIELDS = [{"key": "order", "field": "isalesid", "label": "documentPage.order", "fallbackToRouteId": true}, {"key": "customer", "fields": ["custaccount", "invoiceaccount"], "label": "documentPage.customer"}, {"key": "salesId", "field": "salesid"}, {"key": "name", "fields": ["salesname", "name"], "label": "documentPage.name"}, {"key": "status", "fields": ["salesstatus", "idaxsalesstatus", "documentstatus"], "label": "documentPage.status", "resolveEnumLabel": true}, {"key": "requestedDelivDate", "fields": ["requesteddelivdate", "requesteddelivdateorig"], "type": "date"}, {"key": "purchOrderFormNum", "field": "purchorderformnum"}, {"key": "dataArea", "field": "dataareaid", "label": "documentPage.dataArea", "showOnlyWhenDataArea": true}];
const LINE_PARENT_FIELD = "iSalesId";
const LINE_DATA_AREA_FIELD = "dataAreaId";
const HEADER_DEFAULT_FILTER_FIELDS = ["iSalesId", "custaccount", "salesname", "salesstatus", "idaxdatesales", "purchorderformnum", "volteado"];
const LINE_DEFAULT_FILTER_FIELDS = ["itemId"];
const DOCUMENT_ENTITY_NAME = "salesOrders";

// Joins that duplicate the header row once per joined child row (e.g. the
// salesLine join reused by this document's header grid via the underlying
// table service). See table-config.yml tables.<header_table>.joins.<key>.
// frontend.groupDuplicateRows and CrudManager's joinRowGroupingDefaults prop.
const HEADER_JOIN_ROW_GROUPING_DEFAULTS = {"salesLine": true};
const HEADER_GRID_ROW_STYLE_RULES = [{"hover_text": "Pedido sin confirmar", "style": {"backgroundColor": "#fd5c5a"}, "when": {"field": "documentstatus", "operator": "lt", "value": 3}}, {"hover_text": "Pedido sin orden de manipulacion", "style": {"backgroundColor": "#cfe2ff"}, "when": {"field": "idaxhasmanipulationorder", "operator": "eq", "value": false}}, {"hover_text": "Pedido sin listado de direccion (confirmado al abrirlo)", "style": {"backgroundColor": "#ffd59e"}, "when": {"field": "idaxhasdirectionreport", "operator": "eq", "value": false}}];
const LINE_GRID_ROW_STYLE_RULES = [{"hover_text": "Art\u00edculo pendiente de crear o resolver", "style": {"backgroundColor": "#fd5c5a"}, "when": {"field": "itemId", "operator": "ends_with", "value": "XXXX"}}, {"hover_text": "Precio de venta sin informar o a cero", "style": {"backgroundColor": "#ffd59e"}, "when": {"field": "salesPrice", "operator": "blank_or_zero"}}, {"hover_text": "Cantidad de venta sin informar o a cero", "style": {"backgroundColor": "#ffd59e"}, "when": {"field": "salesQty", "operator": "blank_or_zero"}}, {"hover_text": "El importe total no coincide con el esperado por el cliente (+/-2%)", "style": {"backgroundColor": "#ffd6e7"}, "when": {"field": "custlineamount", "operator": "outside_percent_tolerance", "tolerance": 0.02, "value_field": "lineAmount"}}, {"hover_text": "Tarifa inferior al precio marcado por el cliente", "style": {"backgroundColor": "#e9d5ff"}, "when": {"field": "salesPrice", "operator": "lt", "value_field": "custprice"}}, {"hover_text": "Precio de venta diferente del precio marcado por el cliente", "style": {"backgroundColor": "#fff3cd"}, "when": {"field": "custprice", "operator": "ne_present", "value_field": "salesPrice"}}, {"hover_text": "Art\u00edculo a usar diferente", "style": {"backgroundColor": "#BAE1E1"}, "when": {"field": "itemidcust", "operator": "ne_present", "value_field": "itemId"}}];

const createGridRowStyleResolver = (rules) => (row) => {
  if (!row || typeof row !== "object") {
    return null;
  }

  const toFiniteNumber = (value) => {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const evaluate = (left, operator, right, tolerance = 0) => {
    const leftNumber = toFiniteNumber(left);
    const rightNumber = toFiniteNumber(right);
    const bothNumeric = leftNumber !== null && rightNumber !== null;
    const leftBlank = left === undefined || left === null || left === "";
    const rightBlank = right === undefined || right === null || right === "";
    switch (operator) {
      case "blank":
        return leftBlank;
      case "present":
        return !leftBlank;
      case "blank_or_zero":
        return leftBlank || (leftNumber !== null && Math.abs(leftNumber) <= 0.0001);
      case "eq":
        if (bothNumeric) return Math.abs(leftNumber - rightNumber) <= 0.0001;
        return String(left ?? "") === String(right ?? "");
      case "ne":
        if (bothNumeric) return Math.abs(leftNumber - rightNumber) > 0.0001;
        return String(left ?? "") !== String(right ?? "");
      case "ne_present":
        if (leftBlank || rightBlank) return false;
        if (bothNumeric) return Math.abs(leftNumber - rightNumber) > 0.0001;
        return String(left ?? "") !== String(right ?? "");
      case "outside_percent_tolerance":
        if (!bothNumeric || Math.abs(rightNumber) <= 0.0001) return false;
        return Math.abs(leftNumber - rightNumber) > Math.abs(rightNumber) * Number(tolerance || 0);
      case "gt":
        return Number(left) > Number(right);
      case "gte":
        return Number(left) >= Number(right);
      case "lt":
        return Number(left) < Number(right);
      case "lte":
        return Number(left) <= Number(right);
      case "in":
        return Array.isArray(right) && right.map((v) => String(v)).includes(String(left ?? ""));
      case "not_in":
        return Array.isArray(right) && !right.map((v) => String(v)).includes(String(left ?? ""));
      case "contains":
        return String(left ?? "").toLowerCase().includes(String(right ?? "").toLowerCase());
      case "starts_with":
        return String(left ?? "").toLowerCase().startsWith(String(right ?? "").toLowerCase());
      case "ends_with":
        return String(left ?? "").toLowerCase().endsWith(String(right ?? "").toLowerCase());
      default:
        return false;
    }
  };

  for (const rule of rules) {
    const field = rule?.when?.field;
    const operator = rule?.when?.operator || "eq";
    const value = rule?.when?.value_field
      ? getRowFieldValue(row, rule.when.value_field)
      : rule?.when?.value;
    if (!field) {
      continue;
    }
    const fieldValue = getRowFieldValue(row, field);
    if (evaluate(fieldValue, operator, value, rule?.when?.tolerance ?? rule?.tolerance)) {
      return rule?.hover_text
        ? { style: rule?.style || null, title: rule.hover_text }
        : rule?.style || null;
    }
  }

  return null;
};
export const resolveHeaderGridRowStyle = createGridRowStyleResolver(HEADER_GRID_ROW_STYLE_RULES);
export const resolveLineGridRowStyle = createGridRowStyleResolver(LINE_GRID_ROW_STYLE_RULES);

const normalizeFieldKey = (value) =>
  (value || "").toString().replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

const hasUsableValue = (value) =>
  value !== undefined && value !== null && value !== "";

const getRowFieldValue = (row, fieldName) => {
  if (!row || typeof row !== "object" || !fieldName) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(row, fieldName)) {
    return row[fieldName];
  }

  const normalizedTarget = normalizeFieldKey(fieldName);
  const rowKey = Object.keys(row || {}).find(
    (key) => normalizeFieldKey(key) === normalizedTarget,
  );
  if (rowKey) {
    return row[rowKey];
  }

  if (row.id && typeof row.id === "object") {
    if (Object.prototype.hasOwnProperty.call(row.id, fieldName)) {
      return row.id[fieldName];
    }
    const idKey = Object.keys(row.id || {}).find(
      (key) => normalizeFieldKey(key) === normalizedTarget,
    );
    if (idKey) {
      return row.id[idKey];
    }
  }

  return null;
};

const applyDocumentLineIdentity = (record, headerIdValue, dataAreaValue) => {
  if (!record || typeof record !== "object") {
    return record;
  }

  const lineRecord = { ...record };
  const idObj =
    lineRecord.id && typeof lineRecord.id === "object" && !Array.isArray(lineRecord.id)
      ? { ...lineRecord.id }
      : {};

  const lineNumber =
    getRowFieldValue(lineRecord, "lineNum") ||
    getRowFieldValue(idObj, "lineNum");

  if (LINE_PARENT_FIELD && hasUsableValue(headerIdValue)) {
    lineRecord[LINE_PARENT_FIELD] = headerIdValue;
    if (!hasUsableValue(idObj[LINE_PARENT_FIELD])) {
      idObj[LINE_PARENT_FIELD] = headerIdValue;
    }
  }

  if (LINE_DATA_AREA_FIELD && hasUsableValue(dataAreaValue)) {
    lineRecord[LINE_DATA_AREA_FIELD] = dataAreaValue;
    if (!hasUsableValue(idObj[LINE_DATA_AREA_FIELD])) {
      idObj[LINE_DATA_AREA_FIELD] = dataAreaValue;
    }
  }

  if (hasUsableValue(lineNumber) && !hasUsableValue(idObj.lineNum)) {
    idObj.lineNum = lineNumber;
  }

  if (Object.keys(idObj).length > 0) {
    lineRecord.id = idObj;
  }

  return lineRecord;
};

const buildAllowedFilterSet = (defaults = [], extras = []) => {
  const result = new Set();
  [...(defaults || []), ...(extras || [])].forEach((item) => {
    const key = normalizeFieldKey(item);
    if (key) {
      result.add(key);
    }
  });
  return result;
};

const headerPanelTitleStyle = { margin: 0 };
const headerPanelHeadingStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
};
const headerSummaryStyle = {
  marginTop: "10px",
  padding: "10px 12px",
  border: "1px solid #e5e5e5",
  borderRadius: "6px",
  background: "#fafafa",
  display: "flex",
  flexWrap: "wrap",
  gap: "14px",
  alignItems: "center",
};
const headerSummaryItemStyle = {
  display: "flex",
  flexDirection: "column",
  minWidth: "120px",
};
const headerSummaryLabelStyle = {
  fontSize: "11px",
  opacity: 0.75,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};
const headerSummaryValueStyle = {
  fontSize: "14px",
  fontWeight: 600,
};
const headerSummaryActionsStyle = {
  marginLeft: "auto",
  display: "flex",
  gap: "8px",
};

const formatSummaryDate = (value) => {
  if (!value) {
    return "-";
  }
  const asDate = new Date(value);
  if (Number.isNaN(asDate.getTime())) {
    return String(value);
  }
  return asDate.toLocaleDateString();
};

const resolveSummaryFieldLabel = (columns, fieldKeys, fallbackLabel) => {
  const keys = Array.isArray(fieldKeys) ? fieldKeys : [fieldKeys];
  const normalizedKeys = keys.map((key) => normalizeFieldKey(key)).filter(Boolean);
  if (!Array.isArray(columns) || normalizedKeys.length === 0) {
    return fallbackLabel;
  }

  const column = columns.find((col) => {
    if (!col?.key) {
      return false;
    }
    return normalizedKeys.includes(normalizeFieldKey(col.key));
  });

  return column?.label ? column.label : fallbackLabel;
};

const resolveSummaryOptionLabel = (columns, fieldKey, rawValue) => {
  const normalizedField = normalizeFieldKey(fieldKey);
  const column = (Array.isArray(columns) ? columns : []).find(
    (col) => normalizeFieldKey(col?.key) === normalizedField,
  );
  const options = Array.isArray(column?.options) ? column.options : [];
  if (!options.length) {
    return null;
  }

  const rawAsString = rawValue === null || rawValue === undefined ? "" : String(rawValue).trim();
  const match = options.find((opt) => {
    const optionValue = opt?.value;
    const optionAsString = optionValue === null || optionValue === undefined ? "" : String(optionValue).trim();
    return optionAsString === rawAsString;
  });
  return match?.label || null;
};

const buildDocumentDetailRoute = (row) => {
  const headerId = getRowFieldValue(row, HEADER_ID_FIELD);
  if (!headerId) {
    return null;
  }

  const dataArea = DATA_AREA_FIELD
    ? getRowFieldValue(row, DATA_AREA_FIELD)
    : null;
  if (dataArea) {
    return `${DOCUMENT_ROUTE_BASE}/${encodeURIComponent(dataArea)}/${encodeURIComponent(headerId)}`;
  }

  return `${DOCUMENT_ROUTE_BASE}/document/${encodeURIComponent(headerId)}`;
};

export default function SalesOrderDocumentPageGen({ transformHeaderColumns, headerCrudConfigOverride, lineCrudConfigOverride, lineRefreshToken, transformLineColumns, transformLineFormTabs, headerValidateForm, headerOnFormDataChange, headerServiceOverride }) {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dataAreaId, id } = useParams();
  const linesSectionRef = useRef(null);

  const hasSelectedDocument = Boolean(id);
  const documentContextKey = `${dataAreaId || ""}:${id || ""}`;
  const activeHeaderService = headerServiceOverride || salesOrdersService;
  const [isHeaderBrowserCollapsed, setIsHeaderBrowserCollapsed] = useState(hasSelectedDocument);
  const [headerSummary, setHeaderSummary] = useState({
    loading: false,
    error: null,
    row: null,
  });
  const [headerCollapsedAction, setHeaderCollapsedAction] = useState(null);
  const shouldShowHeaderBrowser = !isHeaderBrowserCollapsed;
  const shouldAutoOpenHeaderEdit =
    headerCollapsedAction === "edit" && shouldShowHeaderBrowser && hasSelectedDocument;

  useEffect(() => {
    // In detail mode we collapse header browser by default; in list mode keep it expanded.
    setIsHeaderBrowserCollapsed(hasSelectedDocument);
  }, [hasSelectedDocument, dataAreaId, id]);

  useEffect(() => {
    if (!shouldAutoOpenHeaderEdit) {
      return;
    }
    const timer = setTimeout(() => {
      setHeaderCollapsedAction((current) => (current === "edit" ? null : current));
    }, 900);
    return () => clearTimeout(timer);
  }, [shouldAutoOpenHeaderEdit, documentContextKey]);

  useEffect(() => {
    if (!hasSelectedDocument) {
      return;
    }
    const timer = setTimeout(() => {
      linesSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(timer);
  }, [hasSelectedDocument, dataAreaId, id]);

  useEffect(() => {
    let cancelled = false;

    const loadHeaderSummary = async () => {
      if (!hasSelectedDocument || !id) {
        setHeaderSummary({ loading: false, error: null, row: null });
        return;
      }

      setHeaderSummary((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const filters = { [HEADER_ID_FIELD]: id };
        if (dataAreaId && DATA_AREA_FIELD) {
          filters[DATA_AREA_FIELD] = dataAreaId;
        }
        const result = await activeHeaderService.getAll(0, 1, [], filters);
        const row = Array.isArray(result?.content) ? (result.content[0] || null) : null;
        if (!cancelled) {
          setHeaderSummary({ loading: false, error: null, row });
        }
      } catch (error) {
        if (!cancelled) {
          setHeaderSummary({ loading: false, error: error?.message || t("documentPage.loadingHeaderSummaryError"), row: null });
        }
      }
    };

    loadHeaderSummary();
    return () => {
      cancelled = true;
    };
  }, [hasSelectedDocument, dataAreaId, id, documentContextKey, t, activeHeaderService]);

  const headerAllowedFilters = buildAllowedFilterSet(
    HEADER_DEFAULT_FILTER_FIELDS,
    [HEADER_ID_FIELD, DATA_AREA_FIELD],
  );
  const lineAllowedFilters = buildAllowedFilterSet(
    LINE_DEFAULT_FILTER_FIELDS,
    [LINE_PARENT_FIELD, LINE_DATA_AREA_FIELD],
  );

  const baseHeaderColumns = ([
    {
      key: "__openDocument",
      label: t("documentPage.open"),
      sortable: false,
      showInGrid: true,
      showInForm: false,
      showInFilter: false,
      type: "text",
      render: (row) => {
        const detailRoute = buildDocumentDetailRoute(row);
        if (!detailRoute) {
          return null;
        }
        return <Link to={detailRoute}>{t("documentPage.open")}</Link>;
      }
    },
    {
      key: "iSalesId",
      label: "salesOrders.id",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "salesId",
      label: "salesOrders.salesId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "custaccount",
      label: "salesOrders.customer",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: customersService,
      optionLabel: "name",
      optionValue: "accountNum",
      lookupSelectedLabelField: "accountNum",
      lookupDisplayFields: ["blocked", "accountNum", "name", "namealias", "phone"],
      lookupSearchFields: ["accountNum", "name", "namealias", "phone"],
      lookupSearchMode: "or",
      lookupSort: {"field": "id.accountNum", "direction": "asc"},
      lookupEnumFields: {"blocked": "CustVendorBlocked"},
      lookupValueLabels: {"blocked": {"0": "No", "1": "Factura", "2": "Todo"}}    },
    {
      key: "invoiceaccount",
      label: "salesOrders.invoiceaccount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "salesname",
      label: "salesOrders.name",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverystreet",
      label: "salesOrders.deliverystreet",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverycity",
      label: "salesOrders.deliverycity",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliveryzipcode",
      label: "salesOrders.deliveryzipcode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverycounty",
      label: "salesOrders.deliverycounty",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: countiesService,
      optionLabel: "name",
      optionValue: "countyid",
      lookupSearchMode: "or",
      lookupDependsOn: [{"localField": "deliverycountry", "remoteFilterField": "countryid"}, {"localField": "deliverystate", "remoteFilterField": "stateid"}]    },
    {
      key: "deliverystate",
      label: "salesOrders.deliverystate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: statesService,
      optionLabel: "name",
      optionValue: "stateid",
      lookupSearchMode: "or",
      lookupDependsOn: [{"localField": "deliverycountry", "remoteFilterField": "countryid"}]    },
    {
      key: "deliverycountry",
      label: "salesOrders.deliverycountry",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: countriesService,
      optionLabel: "name",
      optionValue: "countryid",
      lookupSearchMode: "or"    },
    {
      key: "taxgroup",
      label: "salesOrders.taxgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: taxGroupHeadingService,
      optionLabel: "taxgroupname",
      optionValue: "taxgroup",
      lookupSearchMode: "or"    },
    {
      key: "salesgroup",
      label: "salesOrders.salesgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: commissionSalesGroupsService,
      optionLabel: "name",
      optionValue: "groupid",
      lookupSearchMode: "or"    },
    {
      key: "pricegroupid",
      label: "salesOrders.pricegroupid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "select",
      relatedService: priceDiscGroupsService,
      optionLabel: "name",
      optionValue: "groupid",
      lookupSearchMode: "or"    },
    {
      key: "linedisc",
      label: "salesOrders.linedisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "enddisc",
      label: "salesOrders.enddisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxdatesales",
      label: "salesOrders.date",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "date"    },
    {
      key: "salestype",
      label: "salesOrders.salestype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      defaultValue: 3,
      enumName: "SalesType",
      options: [{"value": 0, "label": "Diario", "labelKey": "axEnums.SalesType.0"}, {"value": 1, "label": "Presupuesto", "labelKey": "axEnums.SalesType.1"}, {"value": 2, "label": "Suscripción", "labelKey": "axEnums.SalesType.2"}, {"value": 3, "label": "Pedido de ventas", "labelKey": "axEnums.SalesType.3"}, {"value": 4, "label": "Artículo devuelto", "labelKey": "axEnums.SalesType.4"}, {"value": 5, "label": "Pedido marco", "labelKey": "axEnums.SalesType.5"}, {"value": 6, "label": "Artículos requeridos", "labelKey": "axEnums.SalesType.6"}, {"value": 7, "label": "Información no definida", "labelKey": "axEnums.SalesType.7"}]    },
    {
      key: "salesstatus",
      label: "salesOrders.status",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "select",
      enumName: "SalesStatus",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.SalesStatus.0"}, {"value": 1, "label": "Pedido abierto", "labelKey": "axEnums.SalesStatus.1"}, {"value": 2, "label": "Entregado", "labelKey": "axEnums.SalesStatus.2"}, {"value": 3, "label": "Facturado", "labelKey": "axEnums.SalesStatus.3"}, {"value": 4, "label": "Cancelado", "labelKey": "axEnums.SalesStatus.4"}]    },
    {
      key: "fechaprecio",
      label: "salesOrders.fechaprecio",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "obsinternas",
      label: "salesOrders.obsinternas",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsalbaran",
      label: "salesOrders.obsalbaran",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsfactura",
      label: "salesOrders.obsfactura",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsagente",
      label: "salesOrders.obsagente",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obstransporte",
      label: "salesOrders.obstransporte",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsproforma",
      label: "salesOrders.obsproforma",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxsalesstatus",
      label: "salesOrders.idaxsalesstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "salespoolid",
      label: "salesOrders.salespoolid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salesoriginid",
      label: "salesOrders.salesoriginid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxpsaddrdelivery",
      label: "salesOrders.idaxpsaddrdelivery",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpsaddrinvoice",
      label: "salesOrders.idaxpsaddrinvoice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "multilinedisc",
      label: "salesOrders.multilinedisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliveryname",
      label: "salesOrders.deliveryname",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "dlvmode",
      label: "salesOrders.dlvmode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: deliveryModesService,
      optionLabel: "txt",
      optionValue: "code",
      lookupSearchMode: "or"    },
    {
      key: "markupgroup",
      label: "salesOrders.markupgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "paymmode",
      label: "salesOrders.paymmode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: customerPaymentModesService,
      optionLabel: "name",
      optionValue: "paymmode",
      lookupSearchMode: "or"    },
    {
      key: "paymentsched",
      label: "salesOrders.paymentsched",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "select",
      relatedService: paymentSchedulesService,
      optionLabel: "description",
      optionValue: "name"    },
    {
      key: "idaxpsmodule",
      label: "salesOrders.idaxpsmodule",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxpstotalpaid",
      label: "salesOrders.idaxpstotalpaid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpstotalpaidreal",
      label: "salesOrders.idaxpstotalpaidreal",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpstotalproducts",
      label: "salesOrders.idaxpstotalproducts",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpstotalproductswt",
      label: "salesOrders.idaxpstotalproductswt",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpsconversionrate",
      label: "salesOrders.idaxpsconversionrate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpsidcart",
      label: "salesOrders.idaxpsidcart",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpsidcurrency",
      label: "salesOrders.idaxpsidcurrency",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpsidlang",
      label: "salesOrders.idaxpsidlang",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpsidcarrier",
      label: "salesOrders.idaxpsidcarrier",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "contactpersonid",
      label: "salesOrders.contactpersonid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: contactsService,
      optionLabel: "name",
      optionValue: "contactpersonid",
      lookupSearchMode: "or",
      lookupDependsOn: [{"localField": "custaccount", "remoteFilterField": "custaccount"}]    },
    {
      key: "idaxpackingslipid",
      label: "salesOrders.idaxpackingslipid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "lastinvoice",
      label: "salesOrders.lastinvoice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "lastpackingslipdate",
      label: "salesOrders.lastpackingslipdate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "lastinvoicedate",
      label: "salesOrders.lastinvoicedate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "recId",
      label: "salesOrders.recId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "number"    },
    {
      key: "modifieddate",
      label: "salesOrders.modifieddate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "datetime"    },
    {
      key: "dataAreaId",
      label: "salesOrders.dataAreaId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "isyncstatus",
      label: "salesOrders.isyncstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "deliverydate",
      label: "salesOrders.deliverydate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "deliveryaddress",
      label: "salesOrders.deliveryaddress",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "textarea",
      rows: 5    },
    {
      key: "purchorderformnum",
      label: "salesOrders.purchorderformnum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salestaker",
      label: "salesOrders.salestaker",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "reservation",
      label: "salesOrders.reservation",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "ItemReservation",
      options: [{"value": 0, "label": "Manual", "labelKey": "axEnums.ItemReservation.0"}, {"value": 1, "label": "Automático", "labelKey": "axEnums.ItemReservation.1"}, {"value": 2, "label": "Expansión", "labelKey": "axEnums.ItemReservation.2"}]    },
    {
      key: "freightsliptype",
      label: "salesOrders.freightsliptype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "FreightSlipType",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.FreightSlipType.0"}, {"value": 1, "label": "Porte", "labelKey": "axEnums.FreightSlipType.1"}]    },
    {
      key: "documentstatus",
      label: "salesOrders.documentstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "DocumentStatus",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.DocumentStatus.0"}, {"value": 1, "label": "Presupuesto", "labelKey": "axEnums.DocumentStatus.1"}, {"value": 2, "label": "Pedido de compra", "labelKey": "axEnums.DocumentStatus.2"}, {"value": 3, "label": "Confirmación", "labelKey": "axEnums.DocumentStatus.3"}, {"value": 4, "label": "Preparación del pedido", "labelKey": "axEnums.DocumentStatus.4"}, {"value": 5, "label": "Albarán", "labelKey": "axEnums.DocumentStatus.5"}, {"value": 6, "label": "Lista de recepciones", "labelKey": "axEnums.DocumentStatus.6"}, {"value": 7, "label": "Factura", "labelKey": "axEnums.DocumentStatus.7"}, {"value": 8, "label": "Diario de aprobación de facturas", "labelKey": "axEnums.DocumentStatus.8"}, {"value": 9, "label": "Proyecto - Factura", "labelKey": "axEnums.DocumentStatus.9"}, {"value": 10, "label": "Proyecto - Albarán", "labelKey": "axEnums.DocumentStatus.10"}, {"value": 11, "label": "Presupuesto de CRM", "labelKey": "axEnums.DocumentStatus.11"}, {"value": 12, "label": "Perdido", "labelKey": "axEnums.DocumentStatus.12"}, {"value": 13, "label": "Cancelado", "labelKey": "axEnums.DocumentStatus.13"}]    },
    {
      key: "currencyCode",
      label: "salesOrders.currencyCode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "payment",
      label: "salesOrders.payment",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "cashdisc",
      label: "salesOrders.cashdisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "custgroup",
      label: "salesOrders.custgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "discpercent",
      label: "salesOrders.discpercent",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "customerref",
      label: "salesOrders.customerref",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "listcode",
      label: "salesOrders.listcode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "Listcode",
      options: [{"value": 0, "label": "No incluido", "labelKey": "axEnums.Listcode.0"}, {"value": 1, "label": "Comercio en UE", "labelKey": "axEnums.Listcode.1"}, {"value": 2, "label": "Producción sobre la tasa", "labelKey": "axEnums.Listcode.2"}, {"value": 3, "label": "Comercio comunitario/triangular", "labelKey": "axEnums.Listcode.3"}, {"value": 4, "label": "Triangular/Producción sobre la tasa", "labelKey": "axEnums.Listcode.4"}]    },
    {
      key: "purchId",
      label: "salesOrders.purchId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "postingprofile",
      label: "salesOrders.postingprofile",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "transactioncode",
      label: "salesOrders.transactioncode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverycountryregionid",
      label: "salesOrders.deliverycountryregionid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "onetimecustomer",
      label: "salesOrders.onetimecustomer",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "covstatus",
      label: "salesOrders.covstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "commissiongroup",
      label: "salesOrders.commissiongroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "select",
      relatedService: commissionSalesGroupsService,
      optionLabel: "name",
      optionValue: "groupid",
      lookupSearchMode: "or"    },
    {
      key: "email",
      label: "salesOrders.email",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "email"    },
    {
      key: "freightzone",
      label: "salesOrders.freightzone",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "returnitemnum",
      label: "salesOrders.returnitemnum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "cashdiscpercent",
      label: "salesOrders.cashdiscpercent",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "deadline",
      label: "salesOrders.deadline",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "projid",
      label: "salesOrders.projid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "inventlocationid",
      label: "salesOrders.inventlocationid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "addressreftableid",
      label: "salesOrders.addressreftableid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "vatnum",
      label: "salesOrders.vatnum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "port",
      label: "salesOrders.port",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "incltax",
      label: "salesOrders.incltax",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "numbersequencegroup",
      label: "salesOrders.numbersequencegroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "fixedexchrate",
      label: "salesOrders.fixedexchrate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "languageid",
      label: "salesOrders.languageid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "girotype",
      label: "salesOrders.girotype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "PaymentStub",
      options: [{"value": 0, "label": "Ninguno", "labelKey": "axEnums.PaymentStub.0"}, {"value": 1, "label": "FIK 65", "labelKey": "axEnums.PaymentStub.1"}, {"value": 2, "label": "BBS", "labelKey": "axEnums.PaymentStub.2"}, {"value": 3, "label": "Resguardo azul", "labelKey": "axEnums.PaymentStub.3"}, {"value": 4, "label": "Resguardo rojo", "labelKey": "axEnums.PaymentStub.4"}, {"value": 5, "label": "FIK 762", "labelKey": "axEnums.PaymentStub.5"}, {"value": 6, "label": "Resguardo naranja", "labelKey": "axEnums.PaymentStub.6"}, {"value": 7, "label": "S101", "labelKey": "axEnums.PaymentStub.7"}, {"value": 8, "label": "S102", "labelKey": "axEnums.PaymentStub.8"}, {"value": 9, "label": "Finlandés", "labelKey": "axEnums.PaymentStub.9"}]    },
    {
      key: "printmoduletype",
      label: "salesOrders.printmoduletype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "ModuleCustSalesParameters",
      options: [{"value": 0, "label": "Cliente", "labelKey": "axEnums.ModuleCustSalesParameters.0"}, {"value": 1, "label": "Pedido de ventas", "labelKey": "axEnums.ModuleCustSalesParameters.1"}, {"value": 2, "label": "Parámetros", "labelKey": "axEnums.ModuleCustSalesParameters.2"}, {"value": 3, "label": "Presupuesto", "labelKey": "axEnums.ModuleCustSalesParameters.3"}, {"value": 4, "label": "Usuario", "labelKey": "axEnums.ModuleCustSalesParameters.4"}]    },
    {
      key: "paymspec",
      label: "salesOrders.paymspec",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "fixedduedate",
      label: "salesOrders.fixedduedate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "exportreason",
      label: "salesOrders.exportreason",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "statprocid",
      label: "salesOrders.statprocid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "dlvreason",
      label: "salesOrders.dlvreason",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "quotationid",
      label: "salesOrders.quotationid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverydatecontrol",
      label: "salesOrders.deliverydatecontrol",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "receiptdaterequested",
      label: "salesOrders.receiptdaterequested",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "receiptdateconfirmed",
      label: "salesOrders.receiptdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "shippingdaterequested",
      label: "salesOrders.shippingdaterequested",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "shippingdateconfirmed",
      label: "salesOrders.shippingdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "salesresponsible",
      label: "salesOrders.salesresponsible",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salesunitid",
      label: "salesOrders.salesunitid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "addressrefrecid",
      label: "salesOrders.addressrefrecid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "ajustarsehojas",
      label: "salesOrders.ajustarsehojas",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "albaranvalued",
      label: "salesOrders.albaranvalued",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "confdirection",
      label: "salesOrders.confdirection",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "custobservations",
      label: "salesOrders.custobservations",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "textarea"    },
    {
      key: "daysreservlimit",
      label: "salesOrders.daysreservlimit",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "deliveryphone",
      label: "salesOrders.deliveryphone",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "phone"    },
    {
      key: "diametroexteriorhasta",
      label: "salesOrders.diametroexteriorhasta",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "diametromandril",
      label: "salesOrders.diametromandril",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "directdebitmandate",
      label: "salesOrders.directdebitmandate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "estimate",
      label: "salesOrders.estimate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "invoicingpfp",
      label: "salesOrders.invoicingpfp",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "invoicingtypeid",
      label: "salesOrders.invoicingtypeid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "maxheight",
      label: "salesOrders.maxheight",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "maxweightpallet",
      label: "salesOrders.maxweightpallet",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "netgrossweightlabels",
      label: "salesOrders.netgrossweightlabels",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "paletkinds",
      label: "salesOrders.paletkinds",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "PaletKinds",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.PaletKinds.0"}, {"value": 1, "label": "NONStop (S)", "labelKey": "axEnums.PaletKinds.1"}, {"value": 2, "label": "Doble", "labelKey": "axEnums.PaletKinds.2"}, {"value": 3, "label": "Triple", "labelKey": "axEnums.PaletKinds.3"}, {"value": 4, "label": "Cuadruple", "labelKey": "axEnums.PaletKinds.4"}, {"value": 5, "label": "NONStop", "labelKey": "axEnums.PaletKinds.5"}, {"value": 6, "label": "L NON Stop", "labelKey": "axEnums.PaletKinds.6"}]    },
    {
      key: "palletsimple",
      label: "salesOrders.palletsimple",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "paymdayid",
      label: "salesOrders.paymdayid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "pndconfdirection",
      label: "salesOrders.pndconfdirection",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "printlinedetails",
      label: "salesOrders.printlinedetails",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "proteccionentrematerialypalet",
      label: "salesOrders.proteccionentrematerialypalet",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "warehousetimecalendar",
      label: "salesOrders.warehousetimecalendar",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "cmturgencykind",
      label: "salesOrders.cmturgencykind",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "UrgencyKind",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.UrgencyKind.0"}, {"value": 1, "label": "Primera hora", "labelKey": "axEnums.UrgencyKind.1"}, {"value": 3, "label": "Recoge cliente", "labelKey": "axEnums.UrgencyKind.3"}, {"value": 2, "label": "Agencia", "labelKey": "axEnums.UrgencyKind.2"}, {"value": 4, "label": "Planif tarde", "labelKey": "axEnums.UrgencyKind.4"}, {"value": 5, "label": "Destino Comart", "labelKey": "axEnums.UrgencyKind.5"}, {"value": 6, "label": "Material en depósito", "labelKey": "axEnums.UrgencyKind.6"}, {"value": 7, "label": "Extranjero", "labelKey": "axEnums.UrgencyKind.7"}, {"value": 8, "label": "Proforma", "labelKey": "axEnums.UrgencyKind.8"}, {"value": 9, "label": "Destino Sumapel", "labelKey": "axEnums.UrgencyKind.9"}, {"value": 10, "label": "Destino Besalu", "labelKey": "axEnums.UrgencyKind.10"}, {"value": 11, "label": "Sólo mañanas", "labelKey": "axEnums.UrgencyKind.11"}, {"value": 12, "label": "Consultas ventas", "labelKey": "axEnums.UrgencyKind.12"}, {"value": 13, "label": "Entregas parciales", "labelKey": "axEnums.UrgencyKind.13"}, {"value": 14, "label": "Entrega directa fábrica", "labelKey": "axEnums.UrgencyKind.14"}, {"value": 15, "label": "Requiere Orden Manipulado", "labelKey": "axEnums.UrgencyKind.15"}, {"value": 16, "label": "RESERVA POR CONSIGNA", "labelKey": "axEnums.UrgencyKind.16"}, {"value": 17, "label": "RESERVA POR PROGRAMACIONES", "labelKey": "axEnums.UrgencyKind.17"}, {"value": 18, "label": "Se compra cortado entrega desde Comart", "labelKey": "axEnums.UrgencyKind.18"}, {"value": 19, "label": "Recoge comercial", "labelKey": "axEnums.UrgencyKind.19"}, {"value": 20, "label": "Contabilidad", "labelKey": "axEnums.UrgencyKind.20"}, {"value": 21, "label": "Stock de Cornellà", "labelKey": "axEnums.UrgencyKind.21"}, {"value": 22, "label": "Reposición por reclamación", "labelKey": "axEnums.UrgencyKind.22"}, {"value": 23, "label": "Agencia entrega mismo día", "labelKey": "axEnums.UrgencyKind.23"}, {"value": 24, "label": "Nueva entrada máquina", "labelKey": "axEnums.UrgencyKind.24"}, {"value": 25, "label": "Pendiente de compras", "labelKey": "axEnums.UrgencyKind.25"}, {"value": 26, "label": "Destino manipulador", "labelKey": "axEnums.UrgencyKind.26"}, {"value": 27, "label": "Pendiente cliente", "labelKey": "axEnums.UrgencyKind.27"}, {"value": 28, "label": "Multientregas", "labelKey": "axEnums.UrgencyKind.28"}, {"value": 29, "label": "Falta conforme", "labelKey": "axEnums.UrgencyKind.29"}, {"value": 30, "label": "Recibido cobro Proforma", "labelKey": "axEnums.UrgencyKind.30"}]    },
    {
      key: "requesteddelivdate",
      label: "salesOrders.requesteddelivdate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "requesteddelivdateorig",
      label: "salesOrders.requesteddelivdateorig",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "sourcefilehash",
      label: "salesOrders.sourcefilehash",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "sourcefolderpath",
      label: "salesOrders.sourcefolderpath",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "sourcefilename",
      label: "salesOrders.sourcefilename",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "sourceemailfilename",
      label: "salesOrders.sourceemailfilename",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "email"    },
    {
      key: "sourcejsonfilename",
      label: "salesOrders.sourcejsonfilename",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "volteado",
      label: "salesOrders.volteado",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      defaultValue: 0,
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "modifiedtime",
      label: "salesOrders.modifiedtime",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "number"    },
    {
      key: "modifiedby",
      label: "salesOrders.modifiedby",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "createddate",
      label: "salesOrders.createddate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "datetime"    },
    {
      key: "createdtime",
      label: "salesOrders.createdtime",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "number"    },
    {
      key: "createdby",
      label: "salesOrders.createdby",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "idaxCreatedBy",
      label: "salesOrders.idaxCreatedBy",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "idaxModifiedBy",
      label: "salesOrders.idaxModifiedBy",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "cmtseleccionbobinasestadopv",
      label: "salesOrders.cmtseleccionbobinasestadopv",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "CMTSeleccionBobinasEstadoPV",
      options: [{"value": 0, "label": "No enviado", "labelKey": "axEnums.CMTSeleccionBobinasEstadoPV.0"}, {"value": 1, "label": "Enviado", "labelKey": "axEnums.CMTSeleccionBobinasEstadoPV.1"}, {"value": 2, "label": "Revisado", "labelKey": "axEnums.CMTSeleccionBobinasEstadoPV.2"}, {"value": 3, "label": "Reenviado", "labelKey": "axEnums.CMTSeleccionBobinasEstadoPV.3"}]    },
    {
      key: "cmtseleccionbobinasenviadopor",
      label: "salesOrders.cmtseleccionbobinasenviadopor",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxhasdirectionreport",
      label: "salesOrders.idaxhasdirectionreport",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "checkbox"    },
    {
      key: "idaxhasmanipulationorder",
      label: "salesOrders.idaxhasmanipulationorder",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "checkbox"    },
    {
      key: "salesLine.lineNum",
      label: "salesOrderLines.lineNum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.itemId",
      label: "salesOrderLines.itemId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.name",
      label: "salesOrderLines.name",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.externalitemid",
      label: "salesOrderLines.externalitemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.typeitemid",
      label: "salesOrderLines.typeitemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.gramajeitemid",
      label: "salesOrderLines.gramajeitemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.venditemid",
      label: "salesOrderLines.venditemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.salesQty",
      label: "salesOrderLines.salesQty",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.qtyOrdered",
      label: "salesOrderLines.qtyOrdered",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.salesunit",
      label: "salesOrderLines.salesunit",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.salesPrice",
      label: "salesOrderLines.salesPrice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.lineamount",
      label: "salesOrderLines.lineamount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.width",
      label: "salesOrderLines.width",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.depth",
      label: "salesOrderLines.depth",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.inventtransid",
      label: "salesOrderLines.inventtransid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.confirmeddlv",
      label: "salesOrderLines.confirmeddlv",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "datetime",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.receiptdateconfirmed",
      label: "salesOrderLines.receiptdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "datetime",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
    {
      key: "salesLine.shippingdateconfirmed",
      label: "salesOrderLines.shippingdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "datetime",
      sourceJoin: "salesLine",
      table: "salesLine",
      tableLabel: "salesLines.title"    },
  ]).map((col) => {
    if (!col || !col.key || col.key === "__openDocument") {
      return col;
    }
    return {
      ...col,
      showInFilter: headerAllowedFilters.has(normalizeFieldKey(col.key)),
    };
  });

  const baseLineColumns = ([
    {
      key: "iSalesId",
      label: "salesOrderLines.iSalesId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "salesId",
      label: "salesOrderLines.salesId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "lineNum",
      label: "salesLines.line",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpsid",
      label: "salesOrderLines.idaxpsid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxpsidwarehouse",
      label: "salesOrderLines.idaxpsidwarehouse",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "custaccount",
      label: "salesOrderLines.custaccount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "itemId",
      label: "salesLines.item",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: itemsService,
      optionLabel: "itemname",
      optionValue: "itemId",
      lookupSelectedLabelField: "itemId",
      lookupDisplayFields: ["itemId", "itemname", "inventtypeitemid", "inventvenditemid", "inventgramajeitemid", "width", "depth"],
      lookupSearchFields: ["itemId", "itemname", "inventtypeitemid", "inventvenditemid", "inventgramajeitemid", "width", "depth"],
      lookupSearchMode: "and",
      lookupSort: {"field": "itemId", "direction": "asc"}    },
    {
      key: "name",
      label: "salesOrderLines.name",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "presentacion",
      label: "salesOrderLines.presentacion",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salesQty",
      label: "salesLines.qty",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "salesPrice",
      label: "salesLines.price",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "linepercent",
      label: "salesOrderLines.linepercent",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "lineamount",
      label: "salesLines.amount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "preciotarifa",
      label: "salesOrderLines.preciotarifa",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "defaultprecio",
      label: "salesOrderLines.defaultprecio",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "lineamounttarifa",
      label: "salesOrderLines.lineamounttarifa",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "itemsuperficie",
      label: "salesOrderLines.itemsuperficie",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "idaxdatesales",
      label: "salesOrderLines.idaxdatesales",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "modifieddate",
      label: "salesOrderLines.modifieddate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "datetime"    },
    {
      key: "dataAreaId",
      label: "salesOrderLines.dataAreaId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "isyncstatus",
      label: "salesOrderLines.isyncstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "salesstatus",
      label: "salesOrderLines.salesstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "SalesStatus",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.SalesStatus.0"}, {"value": 1, "label": "Pedido abierto", "labelKey": "axEnums.SalesStatus.1"}, {"value": 2, "label": "Entregado", "labelKey": "axEnums.SalesStatus.2"}, {"value": 3, "label": "Facturado", "labelKey": "axEnums.SalesStatus.3"}, {"value": 4, "label": "Cancelado", "labelKey": "axEnums.SalesStatus.4"}]    },
    {
      key: "ledgeraccount",
      label: "salesOrderLines.ledgeraccount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "externalitemid",
      label: "salesOrderLines.externalitemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "taxgroup",
      label: "salesOrderLines.taxgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "qtyOrdered",
      label: "salesOrderLines.qtyOrdered",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "salesdelivernow",
      label: "salesOrderLines.salesdelivernow",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "remainsalesphysical",
      label: "salesOrderLines.remainsalesphysical",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "remainsalesfinancial",
      label: "salesOrderLines.remainsalesfinancial",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "costprice",
      label: "salesOrderLines.costprice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "currencyCode",
      label: "salesOrderLines.currencyCode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "linedisc",
      label: "salesOrderLines.linedisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "confirmeddlv",
      label: "salesOrderLines.confirmeddlv",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "reservation",
      label: "salesOrderLines.reservation",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "ItemReservation",
      options: [{"value": 0, "label": "Manual", "labelKey": "axEnums.ItemReservation.0"}, {"value": 1, "label": "Automático", "labelKey": "axEnums.ItemReservation.1"}, {"value": 2, "label": "Expansión", "labelKey": "axEnums.ItemReservation.2"}]    },
    {
      key: "salesgroup",
      label: "salesOrderLines.salesgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salesunit",
      label: "salesOrderLines.salesunit",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "dimension",
      label: "salesOrderLines.dimension",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "dimension2",
      label: "salesOrderLines.dimension2",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "dimension3",
      label: "salesOrderLines.dimension3",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "priceunit",
      label: "salesOrderLines.priceunit",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "projtransid",
      label: "salesOrderLines.projtransid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "inventtransid",
      label: "salesOrderLines.inventtransid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "custgroup",
      label: "salesOrderLines.custgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salesmarkup",
      label: "salesOrderLines.salesmarkup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "inventdelivernow",
      label: "salesOrderLines.inventdelivernow",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "multilndisc",
      label: "salesOrderLines.multilndisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "multilnpercent",
      label: "salesOrderLines.multilnpercent",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "salestype",
      label: "salesOrderLines.salestype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "SalesType",
      options: [{"value": 0, "label": "Diario", "labelKey": "axEnums.SalesType.0"}, {"value": 1, "label": "Presupuesto", "labelKey": "axEnums.SalesType.1"}, {"value": 2, "label": "Suscripción", "labelKey": "axEnums.SalesType.2"}, {"value": 3, "label": "Pedido de ventas", "labelKey": "axEnums.SalesType.3"}, {"value": 4, "label": "Artículo devuelto", "labelKey": "axEnums.SalesType.4"}, {"value": 5, "label": "Pedido marco", "labelKey": "axEnums.SalesType.5"}, {"value": 6, "label": "Artículos requeridos", "labelKey": "axEnums.SalesType.6"}, {"value": 7, "label": "Información no definida", "labelKey": "axEnums.SalesType.7"}]    },
    {
      key: "blocked",
      label: "salesOrderLines.blocked",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "complete",
      label: "salesOrderLines.complete",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "remaininventphysical",
      label: "salesOrderLines.remaininventphysical",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "transactioncode",
      label: "salesOrderLines.transactioncode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "countyorigdest",
      label: "salesOrderLines.countyorigdest",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "taxitemgroup",
      label: "salesOrderLines.taxitemgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: taxItemGroupHeadingService,
      optionLabel: "name",
      optionValue: "taxitemgroup",
      lookupDisplayFields: ["taxitemgroup", "name"],
      lookupSearchFields: ["taxitemgroup", "name"],
      lookupSearchMode: "and"    },
    {
      key: "taxautogenerated",
      label: "salesOrderLines.taxautogenerated",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "underdeliverypct",
      label: "salesOrderLines.underdeliverypct",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "overdeliverypct",
      label: "salesOrderLines.overdeliverypct",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "barcode",
      label: "salesOrderLines.barcode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "barcodetype",
      label: "salesOrderLines.barcodetype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "inventreftransid",
      label: "salesOrderLines.inventreftransid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "inventreftype",
      label: "salesOrderLines.inventreftype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "InventRefType",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.InventRefType.0"}, {"value": 1, "label": "Pedido de ventas", "labelKey": "axEnums.InventRefType.1"}, {"value": 2, "label": "Pedido de compra", "labelKey": "axEnums.InventRefType.2"}, {"value": 3, "label": "Producción", "labelKey": "axEnums.InventRefType.3"}, {"value": 4, "label": "Línea de producción", "labelKey": "axEnums.InventRefType.4"}, {"value": 5, "label": "Diario de inventario", "labelKey": "axEnums.InventRefType.5"}, {"value": 6, "label": "Presupuesto de CRM", "labelKey": "axEnums.InventRefType.6"}, {"value": 7, "label": "Transferir pedido", "labelKey": "axEnums.InventRefType.7"}]    },
    {
      key: "inventrefid",
      label: "salesOrderLines.inventrefid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverydatecontrol",
      label: "salesOrderLines.deliverydatecontrol",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "dlvmode",
      label: "salesOrderLines.dlvmode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "inventdimid",
      label: "salesOrderLines.inventdimid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "transport",
      label: "salesOrderLines.transport",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "statprocid",
      label: "salesOrderLines.statprocid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "receiptdaterequested",
      label: "salesOrderLines.receiptdaterequested",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "customerlinenum",
      label: "salesOrderLines.customerlinenum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "deliveryaddress",
      label: "salesOrderLines.deliveryaddress",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliveryname",
      label: "salesOrderLines.deliveryname",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverystreet",
      label: "salesOrderLines.deliverystreet",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliveryzipcode",
      label: "salesOrderLines.deliveryzipcode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverycity",
      label: "salesOrderLines.deliverycity",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverycounty",
      label: "salesOrderLines.deliverycounty",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverystate",
      label: "salesOrderLines.deliverystate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverycountryregionid",
      label: "salesOrderLines.deliverycountryregionid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverytype",
      label: "salesOrderLines.deliverytype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "TradeLineDlvType",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.TradeLineDlvType.0"}, {"value": 1, "label": "Entrega directa", "labelKey": "axEnums.TradeLineDlvType.1"}]    },
    {
      key: "customerref",
      label: "salesOrderLines.customerref",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "purchorderformnum",
      label: "salesOrderLines.purchorderformnum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "receiptdateconfirmed",
      label: "salesOrderLines.receiptdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "shippingdaterequested",
      label: "salesOrderLines.shippingdaterequested",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "shippingdateconfirmed",
      label: "salesOrderLines.shippingdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime"    },
    {
      key: "addressrefrecid",
      label: "salesOrderLines.addressrefrecid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "addressreftableid",
      label: "salesOrderLines.addressreftableid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "einvoiceaccountcode",
      label: "salesOrderLines.einvoiceaccountcode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "itemidcust",
      label: "salesOrderLines.itemidcust",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: itemsService,
      optionLabel: "itemname",
      optionValue: "itemId",
      lookupSelectedLabelField: "itemId",
      lookupDisplayFields: ["itemId", "itemname", "inventtypeitemid", "inventvenditemid", "inventgramajeitemid", "width", "depth"],
      lookupSearchFields: ["itemId", "itemname", "inventtypeitemid", "inventvenditemid", "inventgramajeitemid", "width", "depth"],
      lookupSearchMode: "and",
      lookupSort: {"field": "itemId", "direction": "asc"}    },
    {
      key: "depth",
      label: "salesOrderLines.depth",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "depthinvoiced",
      label: "salesOrderLines.depthinvoiced",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "gramajeitemid",
      label: "salesOrderLines.gramajeitemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "label",
      label: "salesOrderLines.label",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "mfoid",
      label: "salesOrderLines.mfoid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "mfoselected",
      label: "salesOrderLines.mfoselected",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "omexped",
      label: "salesOrderLines.omexped",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "textarea"    },
    {
      key: "omname",
      label: "salesOrderLines.omname",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "textarea"    },
    {
      key: "otrosgramajes",
      label: "salesOrderLines.otrosgramajes",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "salesqtyaux",
      label: "salesOrderLines.salesqtyaux",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "salesunitaux",
      label: "salesOrderLines.salesunitaux",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: unitConvertService,
      optionLabel: "tounit",
      optionValue: "tounit",
      lookupSearchFields: ["tounit", "fromunit"],
      lookupSearchMode: "or",
      lookupSort: {"field": "tounit", "direction": "asc"},
      lookupDependsOn: [{"localField": "itemId", "remoteFilterField": "itemId"}],
      lookupOptionValueFields: ["tounit", "fromunit"],
      lookupAllowFreeText: true    },
    {
      key: "typeitemid",
      label: "salesOrderLines.typeitemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "useitemvenddescription",
      label: "salesOrderLines.useitemvenddescription",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "venditemid",
      label: "salesOrderLines.venditemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "width",
      label: "salesOrderLines.width",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "widthinvoiced",
      label: "salesOrderLines.widthinvoiced",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "contrafibra",
      label: "salesOrderLines.contrafibra",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "custprice",
      label: "salesLines.custprice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "number"    },
    {
      key: "volteado",
      label: "salesOrderLines.volteado",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      defaultValue: 0,
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "modifiedtime",
      label: "salesOrderLines.modifiedtime",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "number"    },
    {
      key: "modifiedby",
      label: "salesOrderLines.modifiedby",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "createddate",
      label: "salesOrderLines.createddate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "datetime"    },
    {
      key: "createdtime",
      label: "salesOrderLines.createdtime",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "number"    },
    {
      key: "createdby",
      label: "salesOrderLines.createdby",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "idaxCreatedBy",
      label: "salesOrderLines.idaxCreatedBy",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "idaxModifiedBy",
      label: "salesOrderLines.idaxModifiedBy",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "custlineofbusinessid",
      label: "salesOrderLines.custlineofbusinessid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: lineofbusinessService,
      optionLabel: "description",
      optionValue: "lineofbusinessid",
      lookupDisplayFields: ["lineofbusinessid", "description"],
      lookupSearchFields: ["lineofbusinessid", "description"],
      lookupSearchMode: "and"    },
    {
      key: "custlineamount",
      label: "salesLines.custlineamount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "number"    },
    {
      key: "cmtespecificacalidad",
      label: "salesOrderLines.cmtespecificacalidad",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      enumName: "CMTEspecificaCalidad",
      options: [{"value": 0, "label": "Sí", "labelKey": "axEnums.CMTEspecificaCalidad.0"}, {"value": 1, "label": "No", "labelKey": "axEnums.CMTEspecificaCalidad.1"}]    },
    {
      key: "cmtobsselbobinas",
      label: "salesOrderLines.cmtobsselbobinas",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "otrasfabricas",
      label: "salesOrderLines.otrasfabricas",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      defaultValue: 0,
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "mfonumbobbins",
      label: "salesOrderLines.mfonumbobbins",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number"    },
    {
      key: "recId",
      label: "salesOrderLines.recId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "number"    },
    {
      key: "itemreference",
      label: "salesOrderLines.itemreference",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
  ]).map((col) => {
    if (!col || !col.key) {
      return col;
    }
    return {
      ...col,
      showInFilter: lineAllowedFilters.has(normalizeFieldKey(col.key)),
    };
  });
  const headerColumns = transformHeaderColumns ? transformHeaderColumns(baseHeaderColumns) : baseHeaderColumns;
  const lineColumns = transformLineColumns ? transformLineColumns(baseLineColumns) : baseLineColumns;

  const headerFormTabs = [{"tabId": "TabHeaderOverview", "label": "axFormLayout.salestable.tabs.tabheaderoverview", "order": 1, "groups": [{"groupId": "TabHeaderOverview_MainFields", "label": "axFormLayout.salestable.groups.tabheaderoverview.tabheaderoverview_mainfields", "order": 1, "fields": ["salesId", "purchorderformnum", "custaccount", "invoiceaccount", "currencyCode", "projid"]}], "fields": []}, {"tabId": "TabHeaderSimple", "label": "axFormLayout.salestable.tabs.tabheadersimple", "order": 2, "groups": [{"groupId": "GroupHeader", "label": "axFormLayout.salestable.groups.tabheadersimple.groupheader", "order": 1, "fields": ["contactpersonid", "deliveryname", "deliveryaddress", "salestaker", "salespoolid", "languageid", "onetimecustomer", "documentstatus", "salestype", "sourcefilehash", "sourcefolderpath", "sourcefilename", "sourceemailfilename", "sourcejsonfilename"]}], "fields": []}, {"tabId": "TabHeaderGeneral", "label": "axFormLayout.salestable.tabs.tabheadergeneral", "order": 3, "groups": [{"groupId": "Baseinfo", "label": "axFormLayout.salestable.groups.tabheadergeneral.baseinfo", "order": 1, "fields": ["salesname"]}, {"groupId": "ContactInfo", "label": "axFormLayout.salestable.groups.tabheadergeneral.contactinfo", "order": 2, "fields": ["email"]}, {"groupId": "status", "label": "axFormLayout.salestable.groups.tabheadergeneral.status", "order": 4, "fields": ["deadline"]}, {"groupId": "PrintPackingLineDetails", "label": "axFormLayout.salestable.groups.tabheadergeneral.printpackinglinedetails", "order": 7, "fields": ["printlinedetails"]}], "fields": []}, {"tabId": "TabHeaderSetup", "label": "axFormLayout.salestable.tabs.tabheadersetup", "order": 4, "groups": [{"groupId": "Administration", "label": "axFormLayout.salestable.groups.tabheadersetup.administration", "order": 1, "fields": ["salesunitid", "salesresponsible", "salesoriginid"]}, {"groupId": "Inventory", "label": "axFormLayout.salestable.groups.tabheadersetup.inventory", "order": 2, "fields": ["inventlocationid"]}, {"groupId": "Posting", "label": "axFormLayout.salestable.groups.tabheadersetup.posting", "order": 3, "fields": ["postingprofile", "numbersequencegroup", "invoicingtypeid"]}, {"groupId": "SalesTax", "label": "axFormLayout.salestable.groups.tabheadersetup.salestax", "order": 4, "fields": ["taxgroup", "vatnum", "incltax"]}, {"groupId": "Reservation", "label": "axFormLayout.salestable.groups.tabheadersetup.reservation", "order": 5, "fields": ["reservation"]}, {"groupId": "Commission", "label": "axFormLayout.salestable.groups.tabheadersetup.commission", "order": 6, "fields": ["salesgroup"]}, {"groupId": "GiroMoneyTransferSlip", "label": "axFormLayout.salestable.groups.tabheadersetup.giromoneytransferslip", "order": 7, "fields": ["girotype"]}, {"groupId": "Albaran", "label": "axFormLayout.salestable.groups.tabheadersetup.albaran", "order": 9, "fields": ["albaranvalued"]}], "fields": []}, {"tabId": "TabHeaderAddress", "label": "axFormLayout.salestable.tabs.tabheaderaddress", "order": 5, "groups": [{"groupId": "HeaderDeliveryAddress", "label": "axFormLayout.salestable.groups.tabheaderaddress.headerdeliveryaddress", "order": 1, "fields": ["deliverystreet", "deliveryzipcode", "deliverycity", "deliverycounty", "deliverystate", "deliverycountryregionid", "deliveryphone"]}], "fields": []}, {"tabId": "TabHeaderDelivery", "label": "axFormLayout.salestable.tabs.tabheaderdelivery", "order": 6, "groups": [{"groupId": "Delivery", "label": "axFormLayout.salestable.groups.tabheaderdelivery.delivery", "order": 1, "fields": ["requesteddelivdateorig", "requesteddelivdate", "warehousetimecalendar", "dlvmode", "dlvreason", "exportreason", "cmturgencykind"]}, {"groupId": "Transport", "label": "axFormLayout.salestable.groups.tabheaderdelivery.transport", "order": 2, "fields": ["freightzone", "freightsliptype"]}], "fields": []}, {"tabId": "TabHeaderPriceCalc", "label": "axFormLayout.salestable.tabs.tabheaderpricecalc", "order": 7, "groups": [{"groupId": "Payment", "label": "axFormLayout.salestable.groups.tabheaderpricecalc.payment", "order": 2, "fields": ["payment", "fixedduedate", "paymmode", "paymspec", "paymentsched", "cashdisc", "cashdiscpercent", "paymdayid"]}, {"groupId": "DiscMarkup", "label": "axFormLayout.salestable.groups.tabheaderpricecalc.discmarkup", "order": 3, "fields": ["multilinedisc", "enddisc", "discpercent", "linedisc", "markupgroup"]}], "fields": []}, {"tabId": "TabHeaderOther", "label": "axFormLayout.salestable.tabs.tabheaderother", "order": 9, "groups": [{"groupId": "References", "label": "axFormLayout.salestable.groups.tabheaderother.references", "order": 1, "fields": ["customerref", "returnitemnum", "palletsimple"]}, {"groupId": "HeaderForeignTrade", "label": "axFormLayout.salestable.groups.tabheaderother.headerforeigntrade", "order": 2, "fields": ["transactioncode", "port", "statprocid", "listcode"]}, {"groupId": "Others", "label": "axFormLayout.salestable.groups.tabheaderother.others", "order": 5, "fields": ["pndconfdirection", "confdirection", "daysreservlimit"]}, {"groupId": "CommonParameters_Group", "label": "axFormLayout.salestable.groups.tabheaderother.commonparameters_group", "order": 6, "fields": ["paletkinds", "maxweightpallet", "netgrossweightlabels", "proteccionentrematerialypalet"]}, {"groupId": "SheetParameters_Group", "label": "axFormLayout.salestable.groups.tabheaderother.sheetparameters_group", "order": 7, "fields": ["maxheight", "volteado", "ajustarsehojas"]}, {"groupId": "ReelParameters_Group", "label": "axFormLayout.salestable.groups.tabheaderother.reelparameters_group", "order": 8, "fields": ["diametromandril", "diametroexteriorhasta"]}], "fields": []}, {"tabId": "CMT_Comments", "label": "axFormLayout.salestable.tabs.cmt_comments", "order": 10, "groups": [{"groupId": "Observations", "label": "axFormLayout.salestable.groups.cmt_comments.observations", "order": 1, "fields": ["custobservations"]}], "fields": []}];
  const lineFormTabs = [{"tabId": "TabLineOverview", "label": "axFormLayout.salesline.tabs.tablineoverview", "order": 13, "groups": [{"groupId": "TabLineOverview_MainFields", "label": "axFormLayout.salesline.groups.tablineoverview.tablineoverview_mainfields", "order": 1, "fields": ["name", "cmtespecificacalidad", "itemId", "typeitemid", "gramajeitemid", "venditemid", "salesqtyaux", "salesunitaux", "salesQty", "salesunit", "salesPrice", "linedisc", "linepercent", "lineamount", "width", "depth", "mfoid", "mfonumbobbins", "mfoselected"]}], "fields": []}, {"tabId": "TabLineGeneral", "label": "axFormLayout.salesline.tabs.tablinegeneral", "order": 14, "groups": [{"groupId": "Sales", "label": "axFormLayout.salesline.groups.tablinegeneral.sales", "order": 1, "fields": ["externalitemid", "itemreference", "volteado"]}, {"groupId": "ItemCustGroup", "label": "axFormLayout.salesline.groups.tablinegeneral.itemcustgroup", "order": 2, "fields": ["itemidcust", "useitemvenddescription"]}, {"groupId": "SalesLineStatusGroup", "label": "axFormLayout.salesline.groups.tablinegeneral.saleslinestatusgroup", "order": 4, "fields": ["salesstatus", "blocked", "complete"]}, {"groupId": "EInvoiceLine", "label": "axFormLayout.salesline.groups.tablinegeneral.einvoiceline", "order": 5, "fields": ["einvoiceaccountcode"]}, {"groupId": "ValueInvoiced", "label": "axFormLayout.salesline.groups.tablinegeneral.valueinvoiced", "order": 8, "fields": ["widthinvoiced", "depthinvoiced"]}], "fields": []}, {"tabId": "TabLineSetup", "label": "axFormLayout.salesline.tabs.tablinesetup", "order": 15, "groups": [{"groupId": "LineInventory", "label": "axFormLayout.salesline.groups.tablinesetup.lineinventory", "order": 1, "fields": ["inventtransid", "reservation", "qtyOrdered", "remaininventphysical", "inventdelivernow"]}, {"groupId": "Return", "label": "axFormLayout.salesline.groups.tablinesetup.return", "order": 4, "fields": ["costprice"]}, {"groupId": "SalesLineDeliveryGroup", "label": "axFormLayout.salesline.groups.tablinesetup.saleslinedeliverygroup", "order": 5, "fields": ["overdeliverypct", "underdeliverypct", "receiptdaterequested", "shippingdaterequested", "receiptdateconfirmed", "shippingdateconfirmed", "deliverydatecontrol", "dlvmode", "deliverytype"]}, {"groupId": "LinePosting", "label": "axFormLayout.salesline.groups.tablinesetup.lineposting", "order": 6, "fields": ["ledgeraccount"]}, {"groupId": "LineSalesTax", "label": "axFormLayout.salesline.groups.tablinesetup.linesalestax", "order": 7, "fields": ["taxitemgroup", "taxgroup"]}, {"groupId": "LineCommission", "label": "axFormLayout.salesline.groups.tablinesetup.linecommission", "order": 8, "fields": ["salesgroup"]}], "fields": []}, {"tabId": "TabPageAddress", "label": "axFormLayout.salesline.tabs.tabpageaddress", "order": 16, "groups": [{"groupId": "LineDeliveryAddress", "label": "axFormLayout.salesline.groups.tabpageaddress.linedeliveryaddress", "order": 1, "fields": ["deliveryname", "deliverystreet", "deliveryzipcode", "deliverycity", "deliverycounty", "deliverystate", "deliverycountryregionid"]}, {"groupId": "LineAddressDisplay", "label": "axFormLayout.salesline.groups.tabpageaddress.lineaddressdisplay", "order": 2, "fields": ["deliveryaddress"]}], "fields": []}, {"tabId": "TabLineQty", "label": "axFormLayout.salesline.tabs.tablineqty", "order": 17, "groups": [{"groupId": "TabLineQty_MainFields", "label": "axFormLayout.salesline.groups.tablineqty.tablineqty_mainfields", "order": 1, "fields": ["salesdelivernow", "remainsalesfinancial", "remainsalesphysical"]}], "fields": []}, {"tabId": "TabLinePriceDisc", "label": "axFormLayout.salesline.tabs.tablinepricedisc", "order": 18, "groups": [{"groupId": "Disc", "label": "axFormLayout.salesline.groups.tablinepricedisc.disc", "order": 1, "fields": ["multilndisc", "multilnpercent"]}, {"groupId": "Prices", "label": "axFormLayout.salesline.groups.tablinepricedisc.prices", "order": 2, "fields": ["priceunit", "salesmarkup"]}], "fields": []}, {"tabId": "TabLineOther", "label": "axFormLayout.salesline.tabs.tablineother", "order": 19, "groups": [{"groupId": "ItemReference", "label": "axFormLayout.salesline.groups.tablineother.itemreference", "order": 1, "fields": ["inventreftype", "inventrefid", "inventreftransid"]}, {"groupId": "Barcode", "label": "axFormLayout.salesline.groups.tablineother.barcode", "order": 3, "fields": ["barcode", "barcodetype"]}, {"groupId": "Externalreference", "label": "axFormLayout.salesline.groups.tablineother.externalreference", "order": 4, "fields": ["customerlinenum", "purchorderformnum", "customerref"]}, {"groupId": "ForeignTrade", "label": "axFormLayout.salesline.groups.tablineother.foreigntrade", "order": 5, "fields": ["custlineofbusinessid", "transactioncode", "transport", "statprocid", "countyorigdest"]}, {"groupId": "Project", "label": "axFormLayout.salesline.groups.tablineother.project", "order": 7, "fields": ["projtransid"]}, {"groupId": "OM", "label": "axFormLayout.salesline.groups.tablineother.om", "order": 8, "fields": ["omname", "omexped"]}, {"groupId": "Label", "label": "axFormLayout.salesline.groups.tablineother.label", "order": 10, "fields": ["label"]}], "fields": []}, {"tabId": "TabObservations", "label": "axFormLayout.salesline.tabs.tabobservations", "order": 20, "groups": [{"groupId": "GroupObservations", "label": "axFormLayout.salesline.groups.tabobservations.groupobservations", "order": 1, "fields": ["cmtobsselbobinas"]}], "fields": []}, {"tabId": "TabLineDimension", "label": "axFormLayout.salesline.tabs.tablinedimension", "order": 21, "groups": [{"groupId": "SalesLineDimensionGroup", "label": "axFormLayout.salesline.groups.tablinedimension.saleslinedimensiongroup", "order": 1, "fields": ["dimension"]}], "fields": []}, {"tabId": "CMT_TabLineSetup", "label": "axFormLayout.salesline.tabs.cmt_tablinesetup", "order": 22, "groups": [{"groupId": "CMT_TabLineSetup_MainFields", "label": "axFormLayout.salesline.groups.cmt_tablinesetup.cmt_tablinesetup_mainfields", "order": 1, "fields": ["contrafibra", "otrosgramajes", "otrasfabricas"]}], "fields": []}];
  const effectiveLineFormTabs = transformLineFormTabs ? transformLineFormTabs(lineFormTabs) : lineFormTabs;
  const headerInitialFormData = headerColumns.reduce((acc, col) => {
    if (
      col &&
      col.showInForm &&
      Object.prototype.hasOwnProperty.call(col, "defaultValue")
    ) {
      acc[col.key] = col.defaultValue;
    }
    return acc;
  }, {});
  const lineInitialFormData = lineColumns.reduce((acc, col) => {
    if (
      col &&
      col.showInForm &&
      Object.prototype.hasOwnProperty.call(col, "defaultValue")
    ) {
      acc[col.key] = col.defaultValue;
    }
    return acc;
  }, {});

  const scopedHeaderService = Object.create(activeHeaderService);
  scopedHeaderService.getAll = async (page = 0, pageSize = 100, sort = [], filters = {}) => {
    const mergedFilters = { ...(filters || {}) };
    // Keep browser behavior in detail routes: never force header id.
    if (dataAreaId && DATA_AREA_FIELD && !mergedFilters[DATA_AREA_FIELD]) {
      mergedFilters[DATA_AREA_FIELD] = dataAreaId;
    }
    if (shouldAutoOpenHeaderEdit && id) {
      mergedFilters[HEADER_ID_FIELD] = id;
    }
    return activeHeaderService.getAll(page, pageSize, sort, mergedFilters);
  };

  const scopedLineService = Object.create(salesOrderLinesService);
  scopedLineService.getAll = async (page = 0, pageSize = 100, sort = [], filters = {}) => {
    if (!hasSelectedDocument || !LINE_PARENT_FIELD) {
      return { content: [], totalElements: 0, totalPages: 0 };
    }

    const mergedFilters = {
      ...(filters || {}),
      [LINE_PARENT_FIELD]: id,
    };

    if (dataAreaId && LINE_DATA_AREA_FIELD) {
      mergedFilters[LINE_DATA_AREA_FIELD] = dataAreaId;
    }

    const result = await salesOrderLinesService.getAll(page, pageSize, sort, mergedFilters);
    const normalizedContent = Array.isArray(result?.content)
      ? result.content.map((item) => applyDocumentLineIdentity(item, id, dataAreaId))
      : [];

    return {
      ...(result || {}),
      content: normalizedContent,
      totalElements:
        result?.totalElements !== undefined
          ? result.totalElements
          : normalizedContent.length,
      totalPages:
        result?.totalPages !== undefined
          ? result.totalPages
          : 1,
    };
  };
  scopedLineService.getAllList = async (page = 0, pageSize = 100, sort = [], filters = {}) => {
    const result = await scopedLineService.getAll(page, pageSize, sort, filters);
    return result?.content || [];
  };
  scopedLineService.create = async (payload) => {
    if (!hasSelectedDocument || !LINE_PARENT_FIELD) {
      throw new Error("Document header is required before creating lines.");
    }

    const linePayload = applyDocumentLineIdentity(payload || {}, id, dataAreaId);

    return salesOrderLinesService.create(linePayload);
  };
  scopedLineService.update = async (lineId, payload) => {
    if (!hasSelectedDocument || !LINE_PARENT_FIELD) {
      throw new Error("Document header is required before updating lines.");
    }

    const normalizedIdRecord = applyDocumentLineIdentity(
      lineId && typeof lineId === "object" ? lineId : {},
      id,
      dataAreaId,
    );
    const normalizedLineId = normalizedIdRecord?.id || lineId;
    const linePayload = applyDocumentLineIdentity(payload || {}, id, dataAreaId);

    return salesOrderLinesService.update(normalizedLineId, linePayload);
  };
  scopedLineService.delete = async (lineId) => {
    if (!hasSelectedDocument || !LINE_PARENT_FIELD) {
      throw new Error("Document header is required before deleting lines.");
    }

    const normalizedIdRecord = applyDocumentLineIdentity(
      lineId && typeof lineId === "object" ? lineId : {},
      id,
      dataAreaId,
    );
    const normalizedLineId = normalizedIdRecord?.id || lineId;
    return salesOrderLinesService.delete(normalizedLineId);
  };

  const lineCrudConfig = {
    canCreate: true && hasSelectedDocument,
    canEdit: true && hasSelectedDocument,
    canDelete: true && hasSelectedDocument,
    gridRowStyleResolver: resolveLineGridRowStyle,
    ...(lineCrudConfigOverride || {}),
  };
  const headerCrudConfig = {
    canCreate: !hasSelectedDocument && true,
    canEdit: true,
    canDelete: !hasSelectedDocument && false,
    gridRowStyleResolver: resolveHeaderGridRowStyle,
    ...(headerCrudConfigOverride || {}),
  };
  const canShowCollapsedHeaderEdit = Boolean(
    hasSelectedDocument &&
      isHeaderBrowserCollapsed &&
      headerCrudConfig.canEdit
  );
  const canShowCollapsedHeaderDelete = Boolean(
    hasSelectedDocument &&
      isHeaderBrowserCollapsed &&
      false
  );

  const resolveHeaderEditId = (row) => {
    if (!row || typeof row !== "object") {
      return null;
    }

    const existingRowId = row.id;

    const headerId = getRowFieldValue(row, HEADER_ID_FIELD);
    const headerIdFromId =
      existingRowId && typeof existingRowId === "object"
        ? getRowFieldValue(existingRowId, HEADER_ID_FIELD)
        : null;
    const effectiveHeaderId = headerId || headerIdFromId;

    if (!effectiveHeaderId) {
      return null;
    }

    if (!DATA_AREA_FIELD) {
      if (existingRowId !== undefined && existingRowId !== null && existingRowId !== "") {
        return existingRowId;
      }
      return effectiveHeaderId;
    }

    const effectiveDataArea =
      dataAreaId ||
      getRowFieldValue(row, DATA_AREA_FIELD) ||
      (existingRowId && typeof existingRowId === "object"
        ? getRowFieldValue(existingRowId, DATA_AREA_FIELD)
        : null);

    if (!effectiveDataArea) {
      return null;
    }

    return {
      [DATA_AREA_FIELD]: effectiveDataArea,
      [HEADER_ID_FIELD]: effectiveHeaderId,
    };
  };

  const handleHeaderRowDoubleClick = (row) => {
    const detailRoute = buildDocumentDetailRoute(row);
    if (detailRoute) {
      navigate(detailRoute);
    }
  };
  const resolveSelectedHeaderId = () => {
    const summaryId = resolveHeaderEditId(summaryRow);
    if (summaryId !== null && summaryId !== undefined && summaryId !== "") {
      return summaryId;
    }
    if (hasSelectedDocument && id) {
      if (DATA_AREA_FIELD && dataAreaId) {
        return {
          [DATA_AREA_FIELD]: dataAreaId,
          [HEADER_ID_FIELD]: id,
        };
      }
      return id;
    }
    return null;
  };
  const handleToggleHeaderBrowser = () => {
    setIsHeaderBrowserCollapsed((prev) => {
      const next = !prev;
      if (next) {
        setHeaderCollapsedAction(null);
      }
      return next;
    });
  };
  const handleCollapsedHeaderEdit = () => {
    setHeaderCollapsedAction("edit");
    setIsHeaderBrowserCollapsed(false);
  };
  const handleCollapsedHeaderDelete = async () => {
    const deleteId = resolveSelectedHeaderId();
    if (!deleteId) {
      return;
    }
    if (!window.confirm(t("documentPage.deleteSelectedHeaderConfirm"))) {
      return;
    }
    try {
      await activeHeaderService.delete(deleteId);
      setHeaderCollapsedAction(null);
      navigate(DOCUMENT_ROUTE_BASE);
    } catch (error) {
      window.alert(error?.message || t("documentPage.deleteSelectedHeaderError"));
    }
  };

  const summaryRow = headerSummary.row;
  const summaryItems = (Array.isArray(HEADER_SUMMARY_FIELDS) ? HEADER_SUMMARY_FIELDS : [])
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const candidateFields = Array.isArray(item.fields)
        ? item.fields
        : (item.field ? [item.field] : []);
      if (!candidateFields.length) {
        return null;
      }

      const isDataAreaItem = candidateFields.some((fieldName) => normalizeFieldKey(fieldName) === normalizeFieldKey(DATA_AREA_FIELD));
      if (item.showOnlyWhenDataArea && !DATA_AREA_FIELD) {
        return null;
      }

      let rawValue = null;
      for (const fieldName of candidateFields) {
        rawValue = getRowFieldValue(summaryRow, fieldName);
        if (rawValue !== null && rawValue !== undefined && rawValue !== "") {
          break;
        }
      }

      if ((rawValue === null || rawValue === undefined || rawValue === "") && item.fallbackToRouteId) {
        rawValue = id || null;
      }
      if ((rawValue === null || rawValue === undefined || rawValue === "") && isDataAreaItem) {
        rawValue = dataAreaId || null;
      }

      let displayValue = rawValue;
      if (item.type === "date") {
        displayValue = formatSummaryDate(rawValue);
      } else if (item.resolveEnumLabel) {
        const enumLabelKey = resolveSummaryOptionLabel(headerColumns, candidateFields[0], rawValue);
        if (enumLabelKey) {
          displayValue = t(enumLabelKey);
        }
      }

      const labelKey = item.label || resolveSummaryFieldLabel(headerColumns, candidateFields, item.fallbackLabel || candidateFields[0]);
      return {
        key: item.key || candidateFields.join("|"),
        label: t(labelKey),
        value:
          displayValue === null || displayValue === undefined || displayValue === ""
            ? "-"
            : displayValue,
      };
    })
    .filter(Boolean);

  return (
    <div className="document-page">
      <PageHeader
        title={t(`${DOCUMENT_ENTITY_NAME}.title`)}
        subtitle={t(`${DOCUMENT_ENTITY_NAME}.subtitle`)}
        icon={faFileInvoice}
      />
      <section className="panel">
        <header className="panel-heading">
          <div style={headerPanelHeadingStyle}>
            <h2 className="panel-title" style={headerPanelTitleStyle}>{t("documentPage.documentHeader")}</h2>
            {hasSelectedDocument && !isHeaderBrowserCollapsed && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleToggleHeaderBrowser}
              >
                {t("documentPage.lockEditing")}
              </button>
            )}
          </div>
          {hasSelectedDocument && isHeaderBrowserCollapsed && (
            <div style={headerSummaryStyle}>
              {headerSummary.loading ? (
                <span>{t("documentPage.loadingHeaderSummary")}</span>
              ) : headerSummary.error ? (
                <span>{headerSummary.error}</span>
              ) : (
                <>
                  {summaryItems.map((summaryItem) => (
                    <div key={summaryItem.key} style={headerSummaryItemStyle}>
                      <span style={headerSummaryLabelStyle}>{summaryItem.label}</span>
                      <span style={headerSummaryValueStyle}>{summaryItem.value}</span>
                    </div>
                  ))}
                  {(canShowCollapsedHeaderEdit || canShowCollapsedHeaderDelete) && (
                    <div style={headerSummaryActionsStyle}>
                      {canShowCollapsedHeaderEdit && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={handleCollapsedHeaderEdit}
                        >
                          {t("common.edit")}
                        </button>
                      )}
                      {canShowCollapsedHeaderDelete && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={handleCollapsedHeaderDelete}
                        >
                          {t("common.delete")}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </header>
      </section>

      {!isHeaderBrowserCollapsed && (
        <CrudManager
          key={`header-browser:${documentContextKey}`}
          service={scopedHeaderService}
          columns={headerColumns}
          entityName="salesOrders"
          permissionPrefix="legacy.salesorder"
          formLayout={shouldAutoOpenHeaderEdit ? "inline" : "page"}
          formTabs={headerFormTabs}
          canCreate={headerCrudConfig.canCreate}
          canEdit={headerCrudConfig.canEdit}
          canDelete={headerCrudConfig.canDelete}
          canFilter={!hasSelectedDocument}
          initialFormData={headerInitialFormData}
          validateForm={headerValidateForm}
          onFormDataChange={headerOnFormDataChange}
          modalClassName={headerCrudConfigOverride?.modalClassName || ""}
          autoOpenSingleRowOnLoad={shouldAutoOpenHeaderEdit}
          autoOpenContextKey={shouldAutoOpenHeaderEdit ? `header-edit:${documentContextKey}` : "header-browser"}
          autoOpenRowIdResolver={resolveHeaderEditId}
          onRowDoubleClick={handleHeaderRowDoubleClick}
          gridRowStyleResolver={headerCrudConfig.gridRowStyleResolver || null}
          joinRowGroupingDefaults={HEADER_JOIN_ROW_GROUPING_DEFAULTS}
          renderForm={headerCrudConfig.renderForm || null}
          showPageHeader={false}
        />
      )}

      <div className="mt-4">
        <section className="panel" ref={linesSectionRef}>
          <header className="panel-heading">
            <h2 className="panel-title">{t("documentPage.documentLines")}</h2>
          </header>
          <div className="panel-body">
            {!hasSelectedDocument ? (
              <div>
                {t("documentPage.openInstructionPrefix")} <strong>{t("documentPage.open")}</strong>{" "}
                {t("documentPage.openInstructionSuffix")}
              </div>
            ) : (
              <CrudManager
                key={`lines:${documentContextKey}`}
                service={scopedLineService}
                columns={lineColumns}
                entityName="salesOrderLines"
                permissionPrefix="legacy.salesOrderLines"
                fieldPermissionPrefix="legacy.salesOrderLines"
                formLayout="modal"
                formTabs={effectiveLineFormTabs}
                canCreate={lineCrudConfig.canCreate}
                canEdit={lineCrudConfig.canEdit}
                canDelete={lineCrudConfig.canDelete}
                canFilter={false}
                allowFilterConfiguration={false}
                initialFormData={lineInitialFormData}
                modalClassName={lineCrudConfig.modalClassName || ""}
                gridRowStyleResolver={lineCrudConfig.gridRowStyleResolver}
                renderRowActions={lineCrudConfig.renderRowActions}
                renderCreateActions={lineCrudConfig.renderCreateActions}
                onFormDataChange={lineCrudConfig.onFormDataChange}
                onFormOpen={lineCrudConfig.onFormOpen}
                externalRefreshToken={lineRefreshToken}
                showPageHeader={false}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}